import { useMemo, type JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, Inline, Stack, StatePill, type StatePillTone } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";

import { ApiError, apiGet } from "../../../lib/api.js";
import { useGtccHeadTabs } from "../control/HeadTabsContext.js";

/**
 * A-GTCC-ST5 — Domain Routing Health im Cockpit (Register Zeile 33), NUR LESEND.
 *
 * Vorlage ist die gtcc1-Seite
 * `apps/admin/src/domain-routing/DomainRoutingPage.tsx:164`.
 * Gelesen wird derselbe, bereits vorhandene Plattform-Owner-Endpunkt
 * `GET /v1/admin/domain-routing/health`
 * (`services/api/src/modules/admin/domain-routing-health.routes.ts:111`).
 * Die Route ist der einzige Handler des Moduls und hat keinen POST.
 *
 * Bewusst NICHT enthalten: das Host-Probe-Formular der Vorlage. Es ist ein
 * Bedienelement (`?host=` als Query), und die Sicht soll wie ST1–ST4
 * genau eine parameterlose Anfrage ausloesen. Damit ist `probe` in der
 * Antwort immer `null` und wird hier nicht gerendert. Ebenfalls weg: der
 * Retry-Knopf.
 *
 * i18n: Diese Aenderung fasst keine Katalogdatei an (Instruktion, vom
 * Orchestrator bestaetigt). Gemessen gibt es keine Schluessel
 * `admin.gtcc.dom.*`, deshalb stehen hier ausschliesslich die bereits
 * vorhandenen Schluessel der Vorlage (`admin.domain_routing.*`) und die
 * generischen (`common.*`, `admin.gtcc.ovw.read_only_note`).
 *
 * Token-Regel (#1748 und Korrektur 01M1X4563S5581Q89Y4Q12TRE4): als
 * Rahmenfarbe ist nur `--gt-sys-border` definiert; die Varianten "muted"
 * und "subtle" sind undefiniert und lassen den Token-Guard rot werden.
 */

type DomainRoutingStatus =
  | "not_configured"
  | "flag_off"
  | "flag_on_base_missing"
  | "ready_inert"
  | "active_app_only";

type DomainRoutingNextStep =
  | "configure_base_host"
  | "configure_canonical_host"
  | "review_allowlist"
  | "enable_strict_host_check"
  | "owner_go_dns_wildcard"
  | "owner_go_caddy_tls"
  | "owner_go_keycloak_redirects"
  | "owner_go_flip_flag";

type DomainRoutingWarning =
  | "strict_host_check_disabled"
  | "flag_on_without_base_host"
  | "tenant_slug_collision_possible";

const STATUSES: readonly DomainRoutingStatus[] = [
  "not_configured",
  "flag_off",
  "flag_on_base_missing",
  "ready_inert",
  "active_app_only",
];

const STATUS_TONE: Record<DomainRoutingStatus, StatePillTone> = {
  not_configured: "neutral",
  flag_off: "neutral",
  flag_on_base_missing: "warn",
  ready_inert: "info",
  active_app_only: "success",
};

interface DomainRoutingReadiness {
  readonly featureEnabled: boolean;
  readonly baseHostConfigured: boolean;
  readonly baseHost: string;
  readonly canonicalHostConfigured: boolean;
  readonly canonicalHost: string;
  readonly allowedHostCount: number;
  readonly strictHostCheck: boolean;
  readonly status: DomainRoutingStatus;
}

interface DomainRoutingHealthResponse {
  readonly readiness: DomainRoutingReadiness;
  readonly nextSteps: readonly DomainRoutingNextStep[];
  readonly warnings: readonly DomainRoutingWarning[];
}

interface ConfigRow {
  readonly key: string;
  readonly label: string;
  readonly value: boolean | string;
}

function isForbidden(error: Error | null): boolean {
  return error instanceof ApiError && error.status === 403;
}

/**
 * Die Antwort kommt als ungetyptes JSON. Nur ein Payload mit einem echten
 * `readiness`-Objekt wird gerendert; alles andere faellt auf den
 * Leerzustand zurueck, statt beim Lesen zu werfen.
 */
function asHealth(
  value: DomainRoutingHealthResponse | undefined,
): DomainRoutingHealthResponse | undefined {
  if (!value) return undefined;
  const candidate = value as Partial<DomainRoutingHealthResponse>;
  const shaped = typeof candidate.readiness === "object" && candidate.readiness !== null;
  return shaped ? value : undefined;
}

/** Ein unbekannter Status aus dem Backend bekommt keinen erfundenen Text. */
function isKnownStatus(value: string): value is DomainRoutingStatus {
  return (STATUSES as readonly string[]).includes(value);
}

export function GtccDomainRoutingView(): JSX.Element {
  const { t } = useLocale();
  const headTabs = useGtccHeadTabs();

  const query = useQuery<DomainRoutingHealthResponse, Error>({
    queryKey: ["gtcc", "dom", "health"],
    queryFn: ({ signal }) =>
      apiGet<DomainRoutingHealthResponse>("/v1/admin/domain-routing/health", undefined, signal),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const data = asHealth(query.data);
  const readiness = data?.readiness;

  const rows = useMemo<readonly ConfigRow[]>(() => {
    if (!readiness) return [];
    const unset = t("admin.domain_routing.config.unset");
    return [
      {
        key: "feature-enabled",
        label: t("admin.domain_routing.config.feature_enabled"),
        value: readiness.featureEnabled,
      },
      {
        key: "base-host",
        label: t("admin.domain_routing.config.base_host"),
        value: readiness.baseHostConfigured ? readiness.baseHost : unset,
      },
      {
        key: "canonical-host",
        label: t("admin.domain_routing.config.canonical_host"),
        value: readiness.canonicalHostConfigured ? readiness.canonicalHost : unset,
      },
      {
        key: "allowlist-count",
        label: t("admin.domain_routing.config.allowlist_count"),
        value: String(readiness.allowedHostCount),
      },
      {
        key: "strict-host-check",
        label: t("admin.domain_routing.config.strict_host_check"),
        value: readiness.strictHostCheck,
      },
    ];
  }, [readiness, t]);

  const nextSteps = data?.nextSteps ?? [];
  const warnings = data?.warnings ?? [];

  return (
    <Stack gap={4} data-testid="gtcc-view-dom" style={{ padding: "var(--gt-sys-space-5)" }}>
      <Stack gap={1}>
        <h2 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-lg)" }}>
          {t("admin.domain_routing.title")}
        </h2>
        <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
          {t("admin.domain_routing.subtitle")}
        </p>
        <Inline gap={2} wrap>
          <StatePill tone="info" label={t("admin.domain_routing.readiness_banner")} />
        </Inline>
        <p
          style={{
            margin: 0,
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
          }}
          data-testid="gtcc-dom-no-actions"
        >
          {t("admin.gtcc.ovw.read_only_note")}
        </p>
      </Stack>

      {headTabs}

      {query.isError ? (
        <Card elevation={1}>
          <p style={{ margin: 0 }} data-testid="gtcc-dom-error">
            {isForbidden(query.error)
              ? t("admin.domain_routing.gate.body")
              : t("admin.domain_routing.error.load_title")}
          </p>
        </Card>
      ) : null}

      {!query.isError && query.isLoading ? (
        <Card elevation={1}>
          <p style={{ margin: 0 }} data-testid="gtcc-dom-loading">
            {t("common.loading")}
          </p>
        </Card>
      ) : null}

      {data && readiness ? (
        <>
          <Card elevation={1}>
            <Stack gap={2} data-testid="gtcc-dom-posture">
              <Inline gap={2} align="center" justify="space-between" wrap>
                <strong>{t("admin.domain_routing.readiness.title")}</strong>
                <StatePill
                  tone={isKnownStatus(readiness.status) ? STATUS_TONE[readiness.status] : "neutral"}
                  label={
                    isKnownStatus(readiness.status)
                      ? t(`admin.domain_routing.status.${readiness.status}` as const)
                      : readiness.status
                  }
                  dataTestId="gtcc-dom-status-pill"
                />
              </Inline>
              {isKnownStatus(readiness.status) ? (
                <span
                  style={{
                    color: "var(--gt-sys-text-muted)",
                    fontSize: "var(--gt-sys-font-size-sm)",
                  }}
                >
                  {t(`admin.domain_routing.status_description.${readiness.status}` as const)}
                </span>
              ) : null}
            </Stack>
          </Card>

          <Card elevation={1}>
            <Stack gap={2} data-testid="gtcc-dom-config">
              <strong>{t("admin.domain_routing.config.title")}</strong>
              <span
                style={{
                  color: "var(--gt-sys-text-muted)",
                  fontSize: "var(--gt-sys-font-size-xs)",
                }}
              >
                {t("admin.domain_routing.config.subtitle")}
              </span>
              <ul
                style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}
                data-testid="gtcc-dom-config-list"
              >
                {rows.map((row) => (
                  <li
                    key={row.key}
                    data-testid={`gtcc-dom-config-row-${row.key}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                      gap: 12,
                      alignItems: "center",
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: "1px solid var(--gt-sys-border)",
                      background: "var(--gt-sys-surface)",
                    }}
                  >
                    <strong style={{ fontSize: "var(--gt-sys-font-size-sm)" }}>{row.label}</strong>
                    <StatePill
                      tone={typeof row.value === "boolean" && row.value ? "info" : "neutral"}
                      label={
                        typeof row.value === "boolean"
                          ? row.value
                            ? t("common.yes")
                            : t("common.no")
                          : row.value
                      }
                      dataTestId={`gtcc-dom-config-pill-${row.key}`}
                    />
                  </li>
                ))}
              </ul>
            </Stack>
          </Card>

          <Card elevation={1}>
            <Stack gap={2} data-testid="gtcc-dom-next-steps">
              <strong>{t("admin.domain_routing.next_steps.title")}</strong>
              <span
                style={{
                  color: "var(--gt-sys-text-muted)",
                  fontSize: "var(--gt-sys-font-size-xs)",
                }}
              >
                {t("admin.domain_routing.next_steps.subtitle")}
              </span>
              {nextSteps.length === 0 ? (
                <span
                  style={{ color: "var(--gt-sys-text-muted)" }}
                  data-testid="gtcc-dom-next-steps-empty"
                >
                  {t("common.none")}
                </span>
              ) : (
                <ul
                  style={{ margin: 0, paddingInlineStart: 20 }}
                  data-testid="gtcc-dom-next-step-list"
                >
                  {nextSteps.map((step) => (
                    <li key={step} data-testid={`gtcc-dom-next-step-${step}`}>
                      {t(`admin.domain_routing.next_step.${step}` as const)}
                    </li>
                  ))}
                </ul>
              )}
            </Stack>
          </Card>

          {warnings.length > 0 ? (
            <Card elevation={1}>
              <Stack gap={2} data-testid="gtcc-dom-warnings">
                {warnings.map((warning) => (
                  <span
                    key={warning}
                    data-testid={`gtcc-dom-warning-${warning}`}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: "1px solid var(--gt-sys-border)",
                      background: "var(--gt-sys-surface)",
                      fontSize: "var(--gt-sys-font-size-sm)",
                    }}
                  >
                    {t(`admin.domain_routing.warning.${warning}` as const)}
                  </span>
                ))}
              </Stack>
            </Card>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}
